import { gl } from '../../Engine';
import { RenderStorage } from '../../core/render/RenderStorage';
import { DoseRenderer } from '../../core/render/renderers/DoseRenderer';
import { Environment } from '../../data/Environment';
import { DoseDistribution } from '../../data/dose/DoseDistribution';
import { paneImageOf, PaneImageSource } from '../paneImage';
import { TextureView } from '../TextureView';
import { Layer } from './Layer';
import { LayerBit } from './LayerBit';

/**
 * Whether `view` draws its Environment's dose: only a view that shows the
 * Environment's primary image, alone or blended. The dose is planned on
 * that image. Over the fused secondary drawn alone its place would rest on
 * a registration the view does not show, so such a view draws none, and
 * neither does a view that shows no image.
 */
export function viewDrawsDose(view: PaneImageSource): boolean {
    return paneImageOf(view).kind === 'primary';
}

/**
 * {@link DoseLayer} provides common properties of {@link Layer Layers} for {@link DoseObject Dose}data.\
 * {@link DoseLayer DoseLayers} have {@link internalFormat} of type {@link gl.RGBA8} and\
 * {@link format} of type {@link gl.RGBA}.
 */
export class DoseLayer extends Layer<DoseDistribution> {
    /**
     * {@link DoseLayer} constructor.
     * @param parent {@link TextureView} on which this {@link DoseLayer} resides.
     */
    public constructor(parent: TextureView, env: Environment) {
        super(parent, env);
    }

    /**
     * The internal format of {@link DoseLayer}s' {@link Layer._texture texture}.\
     * {@link DoseLayer DoseLayers} use this format to support colors.
     */
    public get internalFormat(): number {
        return gl.RGBA8;
    }

    /**
     * The format of the {@link Layer._texture texture} this {@link DoseLayer} defines.
     */
    public get format(): number {
        return gl.RGBA;
    }

    /**
     * The {@link LayerBit.DOSE_LAYER_BIT}
     */
    public get layerBit(): number {
        return LayerBit.DOSE_LAYER_BIT;
    }

    /**
     * Method to retrieve the {@link DoseDistribution DoseDistributions} this {@link DoseLayer} draws.
     * @param environment The {@link Environment} from which the {@link DoseDistribution DoseDistributions} are loaded.
     * @returns The {@link DoseDistribution} loaded in the {@link environment} when the layer's view
     * draws the dose ({@link viewDrawsDose}), otherwise nothing.
     */
    public getTargets(environment: Environment): DoseDistribution[] {
        if (environment.dose && viewDrawsDose(this.getView())) {
            return [environment.dose];
        }
        return [];
    }

    /**
     * Method to draw the given dose distributions on this {@link Layer Layer's} texture.
     * @param doses The {@link DoseDistribution DoseDistributions} that are to be rendered.
     */
    protected draw(doses: DoseDistribution[]): void {
        this.bindAndClearTexture();
        const doseRenderer = RenderStorage.getRenderer(DoseRenderer);
        doseRenderer.render(this.getView(), doses);
    }
}
